import { useKeycloak } from "@react-keycloak/web";
import React, { createContext, useContext, useState } from "react";
import useAirlineAdminApi from "./api/useAirlineAdminApi";
import { VERSION_KEY } from "../utils/constants";
import ROUTE_PATHS from "../utils/routePaths";

const LogoutContext = createContext({
  isSessionExpired: false,
  isUnauthorized: false,
  handleLogout: () => { },
  handleSessionExpired: () => { },
  handleSessionExpiredConfirm: () => { },
  handleUnauthorized: () => { },
  handleUnauthorizedConfirm: () => { },
  logout: () => { },
});

export const useLogout = () => {
  const context = useContext(LogoutContext);
  if (context === undefined) {
    throw new Error("useLogout must be used within a LogoutProvider");
  }
  return context;
};

export const LogoutProvider = ({ children }) => {
  const { keycloak } = useKeycloak();
  const { removeDeviceToken } = useAirlineAdminApi();
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  const logout = () => {
    const versionInfo = localStorage.getItem(VERSION_KEY);
    localStorage.clear();
    sessionStorage.clear();
    if (versionInfo) {
      localStorage.setItem(VERSION_KEY, versionInfo);
    }
    keycloak.logout({
      redirectUri: `${window.location.origin}${ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}`,
    });
  };

  const handleLogout = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        await removeDeviceToken();
      }
    } catch (error) {
      console.error("Error removing device token:", error);
    }
    setIsSessionExpired(false);
    logout();
  };

  const handleUnauthorized = () => {
    setIsUnauthorized(true);
  };

  const handleSessionExpired = () => {
    setIsSessionExpired(true);
  };

  const handleUnauthorizedConfirm = () => {
    setIsUnauthorized(false);
    const versionInfo = localStorage.getItem(VERSION_KEY);
    localStorage.clear();
    sessionStorage.clear();
    if (versionInfo) {
      localStorage.setItem(VERSION_KEY, versionInfo);
    }
    keycloak.logout({
      redirectUri: `${window.location.origin}${ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}`,
    });
  };

  const handleSessionExpiredConfirm = () => {
    setIsSessionExpired(false);
    keycloak.login();
  };

  const value = {
    isSessionExpired,
    isUnauthorized,
    handleLogout,
    handleSessionExpired,
    handleSessionExpiredConfirm,
    handleUnauthorized,
    handleUnauthorizedConfirm,
    logout,
  };

  return (
    <LogoutContext.Provider value={value}>{children}</LogoutContext.Provider>
  );
};
